"use strict";
/**
 * CALL-783: the training arithmetic of the Verschiebung and GA/CB/VVL scenarios, shared verbatim by the
 * backend (which books the cost into months) and the frontend (which shows the resulting total as a
 * read-only grid column and prefills Trainingstage from an entered window) — so the figures on screen can
 * never disagree with the ones the impact calculation charges. Same reason `budgetExtrapolation` lives here.
 *
 * The board writes the total as "Heads × 8 × Trainingstage × €/h + Trainerressource", e.g.
 * 15 agents × 8h × 42 days × €19 + €15 000 = €110 760.
 *
 * There are TWO ways a training window is established, and they are not interchangeable:
 *  - `walkTrainingWorkingDays` walks `trainingDays` working days forward from the start.
 *  - `trainingWorkingDaysBetween` uses an ENTERED Start→Ende span. A Verschiebung row may name its own
 *    `Ende Training` because the walk knows nothing about travel time, and the board shows Ende as a real
 *    date column beside Trainingstage.
 * Either way `trainingDays` — never the span — is the COST multiplier (see trainingTotalCost); the window
 * only decides which months that cost is spread over.
 *
 * Month keys are 'YYYY-MM-DD' first-of-month, so lexicographic order equals chronological order.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.trainingWorkingDayCount = exports.trainingWorkingDaysBetween = exports.walkTrainingWorkingDays = exports.trainingTotalCost = exports.TRAINING_HOURS_PER_DAY = void 0;
/** Standard training day length in hours — the board's constant. */
exports.TRAINING_HOURS_PER_DAY = 8;
/**
 * Total training cost of one row: the per-head hourly cost plus the one-off trainer resource.
 *
 * @param heads        agents/heads to train (Agents, Heads Training)
 * @param trainingDays training days per head (Trainingstage)
 * @param costPerHour  € per training hour (€/Stunde)
 * @param fixedCost    trainer resource / trainer cost, € (Trainerressource)
 */
const trainingTotalCost = (heads, trainingDays, costPerHour, fixedCost) => heads * exports.TRAINING_HOURS_PER_DAY * trainingDays * costPerHour + fixedCost;
exports.trainingTotalCost = trainingTotalCost;
const monthKeyOf = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`;
/**
 * A working day is a Mon–Fri that is not in `holidays` — the national public-holiday dates ('YYYY-MM-DD')
 * the caller injects (CALL-801). The module stays pure: the backend reads them from the publicHoliday
 * table, the frontend from /api/public-holidays, and an empty set falls back to the plain weekday walk.
 */
const isWorkingDay = (date, holidays) => {
    const dow = date.getUTCDay();
    return dow !== 0 && dow !== 6 && !holidays.has(date.toISOString().slice(0, 10));
};
const NO_HOLIDAYS = new Set();
// A pathological input (a mistyped 99999 trainingDays, or an Ende in 2999) must not iterate forever.
// 100 000 days is ~273 years — far past any budget period, so the cap can never truncate a real window.
const WALK_SAFETY_LIMIT = 100000;
/**
 * Walk `trainingDays` working days (Mon–Fri minus `holidays`) forward from `start`, inclusive.
 *
 * Everything is computed in UTC so a date-only value cannot slip a day across a timezone boundary.
 *
 * `trainingDays` rounds to whole days; 0 (or a missing start) means there is no window to spread over —
 * the caller books the whole cost into the start month instead.
 */
const walkTrainingWorkingDays = (start, trainingDays, holidays = NO_HOLIDAYS) => {
    const daysPerMonth = new Map();
    const walkDays = Math.round(trainingDays);
    if (!start || walkDays <= 0) {
        return { endDate: null, daysPerMonth };
    }
    const cursor = new Date(`${start}T00:00:00Z`);
    let placed = 0;
    let lastWorkingDay = null;
    let safety = 0;
    while (placed < walkDays && safety < WALK_SAFETY_LIMIT) {
        if (isWorkingDay(cursor, holidays)) {
            placed += 1;
            lastWorkingDay = new Date(cursor);
            const key = monthKeyOf(cursor);
            daysPerMonth.set(key, (daysPerMonth.get(key) ?? 0) + 1);
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1);
        safety += 1;
    }
    return {
        endDate: lastWorkingDay ? lastWorkingDay.toISOString().slice(0, 10) : null,
        daysPerMonth,
    };
};
exports.walkTrainingWorkingDays = walkTrainingWorkingDays;
/**
 * The working days (Mon–Fri minus `holidays`) of an ENTERED Start→Ende window, inclusive at both ends.
 *
 * The counterpart of the walk above, for a Verschiebung row that names its own `Ende Training`
 * (CALL-783): the client picks the end date because the walk ignores travel time, so the window is
 * entered rather than derived. Same UTC arithmetic as walkTrainingWorkingDays.
 *
 * It decides only the ALLOCATION of the training cost across months; the cost itself still comes from
 * `trainingDays` via trainingTotalCost, so lengthening the window spreads the same euros more thinly
 * rather than charging more of them.
 *
 * An `end` before `start`, or either date missing, yields an empty window — the caller then falls back to
 * booking the whole total into the start month. Validation rejects an inverted pair on both sides before
 * it can be saved, so that is the defensive branch rather than a supported input.
 */
const trainingWorkingDaysBetween = (start, end, holidays = NO_HOLIDAYS) => {
    const daysPerMonth = new Map();
    if (!start || !end || end < start) {
        return { endDate: null, daysPerMonth };
    }
    const cursor = new Date(`${start}T00:00:00Z`);
    const last = new Date(`${end}T00:00:00Z`);
    let lastWorkingDay = null;
    let safety = 0;
    while (cursor.getTime() <= last.getTime() && safety < WALK_SAFETY_LIMIT) {
        if (isWorkingDay(cursor, holidays)) {
            lastWorkingDay = new Date(cursor);
            const key = monthKeyOf(cursor);
            daysPerMonth.set(key, (daysPerMonth.get(key) ?? 0) + 1);
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1);
        safety += 1;
    }
    return {
        endDate: lastWorkingDay ? lastWorkingDay.toISOString().slice(0, 10) : null,
        daysPerMonth,
    };
};
exports.trainingWorkingDaysBetween = trainingWorkingDaysBetween;
/**
 * How many working days an entered Start→Ende window holds — what the grid PREFILLS Trainingstage with
 * once both dates are set (CALL-783).
 *
 * A prefill, not a lock: `trainingDays` is the cost multiplier and stays freely editable afterwards, so a
 * client who trains 15 of the 22 working days in a window types 15 over it. 0 for a window that holds no
 * working day (a Sat→Sun span) or for an incomplete/inverted pair, which the caller reads as "nothing to
 * prefill" and leaves the field alone.
 */
const trainingWorkingDayCount = (start, end, holidays = NO_HOLIDAYS) => {
    let total = 0;
    for (const days of (0, exports.trainingWorkingDaysBetween)(start, end, holidays).daysPerMonth.values())
        total += days;
    return total;
};
exports.trainingWorkingDayCount = trainingWorkingDayCount;
